import { api } from "@/lib/api";
import {
	attentionBadge,
	attentionEmoji,
	cn,
	formatCurrency,
	relativeTime,
} from "@/lib/utils";
import type { AttentionLevel } from "@booking-hub/shared";
import { useQuery } from "@tanstack/react-query";
import {
	AlertTriangle,
	ArrowDownLeft,
	ArrowUpRight,
	Bot,
	CalendarDays,
	LinkIcon,
	Receipt,
	TrendingUp,
} from "lucide-react";
import { Link } from "react-router-dom";

// ============================================================
// Dashboard Home — Übersicht
// ============================================================

interface StatsData {
	data: {
		bookings: {
			total: number;
			active: number;
			thisMonth: number;
			upcoming7Days: number;
		};
		contacts: { total: number };
		companies: { total: number };
		finance: {
			revenueThisMonth: number;
			openInvoices: { count: number; total: number };
		};
	};
}

interface AlertData {
	data: Array<{
		bookingId: string;
		kunde: string;
		level: string;
		reasons: string[];
	}>;
	summary: { critical: number; warning: number; info: number };
}

interface DashboardExtras {
	data: {
		unlinkedVouchers: number;
		recentEmails: Array<{
			id: string;
			direction: string;
			subject: string | null;
			fromEmail: string | null;
			classified: string | null;
			gmailDate: string | null;
			bookingId: string | null;
		}>;
		aiInsights: Array<{
			id: string;
			kunde: string;
			status: string;
			aiSuggestedAction: string | null;
			aiConfidence: number | null;
		}>;
	};
}

export function DashboardPage() {
	const stats = useQuery({
		queryKey: ["stats"],
		queryFn: () => api.get<StatsData>("/stats/overview"),
	});
	const alerts = useQuery({
		queryKey: ["alerts"],
		queryFn: () => api.get<AlertData>("/alerts"),
	});
	const extras = useQuery({
		queryKey: ["dashboard-extras"],
		queryFn: () => api.get<DashboardExtras>("/stats/dashboard-extras"),
	});

	const s = stats.data?.data;
	const a = alerts.data;
	const ex = extras.data?.data;

	return (
		<div className="space-y-6 animate-fade-in">
			<div>
				<h1 className="font-display text-2xl">Dashboard</h1>
				<p className="text-sm text-stone-500">Willkommen zurück.</p>
			</div>

			{/* Stat Cards */}
			<div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
				<StatCard
					icon={CalendarDays}
					label="Buchungen (aktiv)"
					value={s?.bookings.active ?? "—"}
					sub={`${s?.bookings.thisMonth ?? 0} diesen Monat`}
					color="brand"
				/>
				<StatCard
					icon={CalendarDays}
					label="Nächste 7 Tage"
					value={s?.bookings.upcoming7Days ?? "—"}
					sub="anstehend"
					color="emerald"
				/>
				<StatCard
					icon={Receipt}
					label="Offene Rechnungen"
					value={s?.finance.openInvoices.count ?? "—"}
					sub={s ? formatCurrency(s.finance.openInvoices.total) : ""}
					color="amber"
				/>
				<StatCard
					icon={TrendingUp}
					label="Umsatz (Monat)"
					value={s ? formatCurrency(s.finance.revenueThisMonth) : "—"}
					sub=""
					color="green"
				/>
			</div>

			{/* Alerts */}
			{a && (a.summary.critical > 0 || a.summary.warning > 0) && (
				<div className="card">
					<div className="mb-3 flex items-center gap-2">
						<AlertTriangle className="h-4 w-4 text-amber-600" />
						<h2 className="font-medium">Aufmerksamkeit erforderlich</h2>
						{a.summary.critical > 0 && (
							<span className="badge bg-red-100 text-red-800">
								{a.summary.critical} kritisch
							</span>
						)}
						{a.summary.warning > 0 && (
							<span className="badge bg-amber-100 text-amber-800">
								{a.summary.warning} Warnung
							</span>
						)}
					</div>
					<div className="space-y-2">
						{a.data.slice(0, 8).map((alert) => (
							<Link
								key={alert.bookingId}
								to={`/bookings/${alert.bookingId}`}
								className="flex items-start gap-3 rounded-lg px-3 py-2 hover:bg-stone-50 transition-colors"
							>
								<span className="mt-0.5 text-sm">
									{attentionEmoji(alert.level as AttentionLevel)}
								</span>
								<div className="min-w-0 flex-1">
									<div className="text-sm font-medium">{alert.kunde}</div>
									<div className="text-xs text-stone-500">
										{alert.reasons[0]}
									</div>
								</div>
								<span
									className={`badge ${attentionBadge(alert.level as AttentionLevel)}`}
								>
									{alert.level}
								</span>
							</Link>
						))}
					</div>
				</div>
			)}

			{/* Dashboard Extras Row */}
			{ex && (
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
					{/* Unlinked Vouchers */}
					{ex.unlinkedVouchers > 0 && (
						<Link
							to="/lexoffice"
							className="card hover:border-brand-300 transition-colors"
						>
							<div className="flex items-center gap-2 mb-2">
								<LinkIcon size={16} className="text-amber-600" />
								<span className="text-sm font-medium">
									Nicht zugeordnete Belege
								</span>
							</div>
							<div className="text-2xl font-semibold text-amber-600">
								{ex.unlinkedVouchers}
							</div>
							<p className="text-xs text-stone-400 mt-1">
								Belege ohne Buchung zuordnen
							</p>
						</Link>
					)}

					{/* Recent Emails */}
					{ex.recentEmails.length > 0 && (
						<div className="card">
							<h3 className="text-sm font-medium mb-2">Letzte E-Mails</h3>
							<div className="space-y-1.5">
								{ex.recentEmails.map((email) => (
									<div
										key={email.id}
										className="flex items-center gap-2 text-xs"
									>
										<span
											className={cn(
												"shrink-0 rounded-full p-1",
												email.direction === "inbox"
													? "bg-blue-50 text-blue-600"
													: "bg-green-50 text-green-600",
											)}
										>
											{email.direction === "inbox" ? (
												<ArrowDownLeft size={10} />
											) : (
												<ArrowUpRight size={10} />
											)}
										</span>
										<span className="truncate text-stone-700 flex-1">
											{email.subject ?? "(Kein Betreff)"}
										</span>
										<span className="text-stone-400 shrink-0">
											{email.gmailDate ? relativeTime(email.gmailDate) : "—"}
										</span>
									</div>
								))}
							</div>
						</div>
					)}

					{/* AI Insights */}
					{ex.aiInsights.length > 0 && (
						<div className="card">
							<div className="flex items-center gap-2 mb-2">
								<Bot size={16} className="text-brand-600" />
								<h3 className="text-sm font-medium">AI-Empfehlungen</h3>
							</div>
							<div className="space-y-2">
								{ex.aiInsights.map((insight) => (
									<Link
										key={insight.id}
										to={`/bookings/${insight.id}`}
										className="block text-xs hover:bg-stone-50 rounded-md px-2 py-1.5 -mx-2 transition-colors"
									>
										<span className="font-medium text-stone-700">
											{insight.kunde}
										</span>
										<p className="text-stone-500 mt-0.5 truncate">
											{insight.aiSuggestedAction}
										</p>
									</Link>
								))}
							</div>
						</div>
					)}
				</div>
			)}

			{/* Quick Links */}
			<div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
				<Link
					to="/bookings"
					className="card hover:border-brand-300 transition-colors"
				>
					<div className="text-sm font-medium">Alle Buchungen</div>
					<div className="text-xs text-stone-500">
						{s?.bookings.total ?? 0} gesamt
					</div>
				</Link>
				<Link
					to="/contacts"
					className="card hover:border-brand-300 transition-colors"
				>
					<div className="text-sm font-medium">Kontakte</div>
					<div className="text-xs text-stone-500">
						{s?.contacts.total ?? 0} gesamt
					</div>
				</Link>
				<Link
					to="/lexoffice"
					className="card hover:border-brand-300 transition-colors"
				>
					<div className="text-sm font-medium">LexOffice</div>
					<div className="text-xs text-stone-500">Belege & Rechnungen</div>
				</Link>
			</div>
		</div>
	);
}

// ============================================================
// Stat Card
// ============================================================

function StatCard({
	icon: Icon,
	label,
	value,
	sub,
	color,
}: {
	icon: typeof CalendarDays;
	label: string;
	value: string | number;
	sub: string;
	color: string;
}) {
	const bg =
		{
			brand: "bg-brand-50 text-brand-600",
			emerald: "bg-emerald-50 text-emerald-600",
			amber: "bg-amber-50 text-amber-600",
			green: "bg-green-50 text-green-600",
		}[color] ?? "bg-stone-50 text-stone-600";

	return (
		<div className="card">
			<div className={`mb-3 inline-flex rounded-lg p-2 ${bg}`}>
				<Icon size={18} />
			</div>
			<div className="text-2xl font-semibold tracking-tight">{value}</div>
			<div className="text-sm font-medium text-stone-600">{label}</div>
			{sub && <div className="text-xs text-stone-400">{sub}</div>}
		</div>
	);
}
